import { useState } from 'react';
import type { Partner } from '../../lib/partners';

// "Used by" social-proof strip for the login screen.
//
// On the dark art panel every mark is rendered in white, so a row of partners reads as one set
// instead of clashing brand palettes. There are two ways to get there and the difference matters:
//
//   • `logoWhiteSrc` — a real negative version of the mark, used as-is. PREFERRED.
//   • otherwise, `brightness(0) invert(1)` on the colour original. Works on any file with no
//     artwork needed, but it forces EVERY channel to white, which destroys internal contrast: the
//     HRS crest is a coloured ring around a white field with a red horse, and flattening it leaves
//     one featureless disc. UHRS, a solid shape, is unaffected.
//
// The white files in public/partners are derived by scripts/make-white-logos.py, which keys on
// luminance instead — dark ink becomes white, light areas become transparent so the navy shows
// through — which is what keeps the horse and the ring.
//
// On the light panel (the mobile repeat) the colour original is always used; a white logo there
// would be invisible.
interface PartnerLogosProps {
  partners: Partner[];
  label?: string;
  /** Which panel this sits on. `dark` matches the login art panel's fixed palette (it ignores the
   *  active theme), `light` uses the theme tokens like the rest of the app. */
  tone?: 'dark' | 'light';
  className?: string;
}

export function PartnerLogos({ partners, label = 'Used by', tone = 'light', className }: PartnerLogosProps) {
  // Logos are dropped in as static files, so one can legitimately be missing (a partner added to
  // the list before their asset lands). Remember which sources failed and fall back to a name
  // wordmark — a broken-image icon on the sign-in screen looks like the app is broken.
  const [failed, setFailed] = useState<string[]>([]);
  const isDark = tone === 'dark';

  if (partners.length === 0) return null;

  return (
    <div className={className}>
      <div
        className={'mb-3 font-mono text-[11px] uppercase tracking-[0.1em] ' + (isDark ? '' : 'text-muted-2')}
        style={isDark ? { color: 'oklch(0.66 0.012 85)' } : undefined}
      >
        {label}
      </div>
      <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
        {partners.map((partner) => (
          <div key={partner.name} className="flex h-[62px] items-center justify-center">
            {failed.includes(partner.logoSrc) ? (
              // Wordmark fallback, in the colour the logo itself would have been.
              <span
                className={'text-[14px] font-semibold tracking-[-0.01em] ' + (isDark ? '' : 'text-ink')}
                style={isDark ? { color: 'oklch(0.96 0.005 85)' } : undefined}
              >
                {partner.name}
              </span>
            ) : (
              <img
                // Dark panel: the negative file when there is one, the colour original otherwise
                // (flattened by the filter below). Light panel: always the colour original.
                src={isDark ? (partner.logoWhiteSrc ?? partner.logoSrc) : partner.logoSrc}
                alt={partner.name}
                // Explicit height (not max-height) plus shrink-0: as a flex item with `height: auto`
                // an SVG carrying only a viewBox — no width/height attributes, which is most
                // exported logos — collapses to 0×0 in Chrome. A fixed height also lines every logo
                // up on one baseline; max-w + object-contain letterboxes a very wide mark rather
                // than letting it push the row across the panel.
                className="h-[54px] w-auto max-w-[290px] shrink-0 object-contain"
                // Only flatten when falling back to a colour original on the dark panel — applying
                // it to an already-white negative would fill its knocked-out shapes back in.
                style={isDark && !partner.logoWhiteSrc ? { filter: 'brightness(0) invert(1)' } : undefined}
                loading="lazy"
                onError={() =>
                  setFailed((prev) => (prev.includes(partner.logoSrc) ? prev : [...prev, partner.logoSrc]))
                }
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
